import { useState, useEffect } from "react";
import { Receipt, Save, Info, AlertCircle } from "lucide-react";
import { api } from "@/api";
import { validateInvoice } from "./validators.js";
import ActionRequiredBanner from "./ActionRequiredBanner.jsx";

const INVOICE_FIELDS = [
  "invoice_prefix",
  "invoice_next_number",
  "invoice_taxes_enabled",
  "invoice_tax_label_tps",
  "invoice_tax_label_tvq",
  "invoice_tax_number_tps",
  "invoice_tax_number_tvq",
  "default_payment_method",
  "invoice_conditions",
  "invoice_footer",
  "invoice_notes_default",
];

export default function FacturationSection({ settings, update, onSave }) {
  const errors = validateInvoice(settings);
  const isValid = Object.keys(errors).length === 0;
  const prefix = settings.invoice_prefix || "FAC-";
  const taxesOn = !!settings.invoice_taxes_enabled;
  // Batch #14.51 — Détecte les actions requises pour affichage visuel proéminent
  const prefixMissing = !((settings.invoice_prefix || "").trim());

  const [billing, setBilling] = useState(null);
  useEffect(() => {
    let mounted = true;
    api.get("/settings/billing/next-number")
      .then((r) => { if (mounted) setBilling(r.data); })
      .catch(() => { if (mounted) setBilling(null); });
    return () => { mounted = false; };
  }, [settings._ts]);

  const fallbackNext = settings.invoice_next_number || 1001;
  const realNext = billing?.next_number ?? fallbackNext;
  const realCurrent = billing?.last_invoice_number || (realNext > 1 ? `${prefix}${realNext - 1}` : "—");
  const sourceLabel = billing?.source === "db_max"
    ? `Basé sur la dernière facture en base (${billing.last_invoice_number})`
    : "Basé sur les paramètres (aucune facture en base)";

  return (
    <div className="bg-white border rounded-xl p-5 space-y-5" data-testid="facturation-section">
      <h3 className="font-semibold text-sm flex items-center gap-2">
        <Receipt className="w-4 h-4" /> Paramètres de facturation
      </h3>

      {/* Batch #14.52 — Bannière action requise (unifiée via ActionRequiredBanner).
          Affiche automatiquement les issues détectées par /api/settings/health pour
          la section 'invoice', avec quick-fix "Utiliser FAC- par défaut". */}
      <ActionRequiredBanner
        sectionKey="invoice"
        title="Action requise : configurez votre facturation"
        quickFix={prefixMissing ? {
          label: "Utiliser « FAC- » par défaut",
          onClick: () => {
            update("invoice_prefix", "FAC-");
            document.querySelector('[data-testid="settings-invoice-prefix"]')?.focus();
          },
        } : null}
        testId="facturation-action-required-banner"
      />

      {/* Numérotation */}
      <section className="space-y-3 border-l-4 border-blue-200 pl-4">
        <h4 className="text-xs font-bold text-blue-700 uppercase tracking-wider">Numérotation</h4>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className={`block text-sm font-medium mb-1 ${prefixMissing ? "text-rose-700" : ""}`}>
              Préfixe {prefixMissing && <span className="text-rose-600 text-xs">⚠️ requis</span>}
            </label>
            <input
              type="text"
              value={settings.invoice_prefix || ""}
              onChange={(e) => update("invoice_prefix", e.target.value)}
              placeholder="FAC-"
              maxLength={20}
              className={`w-full border rounded-lg px-3 py-2 text-sm font-mono ${prefixMissing ? "border-rose-400 bg-rose-50 ring-2 ring-rose-200 focus:ring-rose-400" : ""}`}
              data-testid="settings-invoice-prefix"
            />
            {errors.invoice_prefix && <p className="text-[10px] text-red-500 mt-0.5">{errors.invoice_prefix}</p>}
            <p className={`text-[10px] mt-0.5 ${prefixMissing ? "text-rose-600 font-semibold" : "text-gray-400"}`}>
              {prefixMissing
                ? "⚠️ À configurer : le préfixe est obligatoire pour vos factures."
                : "Sera ajouté devant le numéro (ex: FAC-1042)."}
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Numéro actuel (DB)</label>
            <input
              type="text"
              value={realCurrent}
              readOnly
              className="w-full border rounded-lg px-3 py-2 text-sm bg-gray-50 text-gray-600"
              data-testid="settings-invoice-current"
            />
            <p className="text-[10px] text-gray-400 mt-0.5">Dernière facture enregistrée.</p>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Prochain numéro (override)</label>
            <input
              type="number"
              min="1"
              max="9999999"
              value={settings.invoice_next_number ?? ""}
              onChange={(e) => update("invoice_next_number", e.target.value ? parseInt(e.target.value, 10) : null)}
              placeholder={String(realNext)}
              className="w-full border rounded-lg px-3 py-2 text-sm font-mono"
              data-testid="settings-invoice-next-input"
            />
            {errors.invoice_next_number && <p className="text-[10px] text-red-500 mt-0.5">{errors.invoice_next_number}</p>}
            <p className="text-[10px] text-gray-400 mt-0.5">{sourceLabel}. Laissez vide pour auto.</p>
          </div>
        </div>
      </section>

      {/* Taxes */}
      <section className="space-y-3 border-l-4 border-emerald-200 pl-4">
        <h4 className="text-xs font-bold text-emerald-700 uppercase tracking-wider">Taxes (TPS / TVQ)</h4>
        <label className="flex items-center gap-3 cursor-pointer" data-testid="settings-taxes-toggle-row">
          <input
            type="checkbox"
            checked={taxesOn}
            onChange={(e) => update("invoice_taxes_enabled", e.target.checked)}
            className="w-4 h-4"
            data-testid="settings-taxes-toggle"
          />
          <div>
            <div className="text-sm font-medium">
              {taxesOn ? "Taxes activées" : "Taxes désactivées"}
            </div>
            <div className="text-[11px] text-gray-500">
              {taxesOn
                ? "TPS 5 % et TVQ 9,975 % seront calculées automatiquement sur les nouvelles factures."
                : "Aucune taxe ne sera ajoutée. Total = sous-total."}
            </div>
          </div>
        </label>

        {taxesOn && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2 pl-7">
            <div>
              <label className="block text-xs font-medium mb-1">Numéro TPS (GST)</label>
              <input
                type="text"
                value={settings.invoice_tax_number_tps || ""}
                onChange={(e) => update("invoice_tax_number_tps", e.target.value)}
                placeholder="123456789 RT0001"
                maxLength={30}
                className="w-full border rounded-lg px-3 py-1.5 text-sm font-mono"
                data-testid="settings-tax-number-tps"
              />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">Numéro TVQ (QST)</label>
              <input
                type="text"
                value={settings.invoice_tax_number_tvq || ""}
                onChange={(e) => update("invoice_tax_number_tvq", e.target.value)}
                placeholder="1234567890 TQ0001"
                maxLength={30}
                className="w-full border rounded-lg px-3 py-1.5 text-sm font-mono"
                data-testid="settings-tax-number-tvq"
              />
            </div>
          </div>
        )}
      </section>

      {/* Batch #14.56 — Relance automatique portail */}
      <section className="space-y-3 border-l-4 border-emerald-200 pl-4">
        <h4 className="text-xs font-bold text-emerald-700 uppercase tracking-wider">
          Relance automatique (portail)
        </h4>
        <p className="text-[11px] text-gray-500">
          Envoie un rappel courriel au client (et au conjoint si lié) si une facture déposée au
          portail n'est <strong>pas ouverte</strong> au bout de N jours.
        </p>

        <label className="flex items-start gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={!!settings.invoice_unread_relance_enabled}
            onChange={(e) => update("invoice_unread_relance_enabled", e.target.checked)}
            className="mt-0.5"
            data-testid="settings-relance-enabled"
          />
          <span className="text-sm">
            Activer la relance automatique des factures non-lues
          </span>
        </label>

        {settings.invoice_unread_relance_enabled && (
          <div className="ml-6 space-y-2 animate-in fade-in slide-in-from-top-1">
            <label className="block text-sm font-medium">
              Délai avant relance
              <input
                type="number"
                min={1}
                max={90}
                value={settings.invoice_unread_relance_days || 7}
                onChange={(e) => update("invoice_unread_relance_days", parseInt(e.target.value, 10) || 7)}
                className="ml-2 w-20 border rounded-lg px-2 py-1 text-sm tabular-nums"
                data-testid="settings-relance-days"
              />
              <span className="ml-2 text-sm text-gray-500">jours après le dépôt au portail</span>
            </label>
            <p className="text-[11px] text-gray-400">
              ⏰ Exécution automatique chaque jour à 09:00 (heure de Montréal). Une seule relance
              par tranche de N jours pour éviter de spammer.
            </p>
          </div>
        )}
      </section>

      {/* Mentions */}
      <section className="space-y-3 border-l-4 border-amber-200 pl-4">
        <h4 className="text-xs font-bold text-amber-700 uppercase tracking-wider">Mentions PDF</h4>
        <div>
          <label className="block text-sm font-medium mb-1">Modes de paiement</label>
          <textarea
            rows={3}
            value={settings.default_payment_method || ""}
            onChange={(e) => update("default_payment_method", e.target.value)}
            className="w-full border rounded-lg px-3 py-2 text-sm"
            placeholder="Virement Interac : votre@courriel.ca&#10;Chèque à l'ordre de : Nom légal"
            maxLength={1000}
            data-testid="settings-payment-method"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Conditions de paiement</label>
          <textarea
            rows={2}
            value={settings.invoice_conditions || ""}
            onChange={(e) => update("invoice_conditions", e.target.value)}
            className="w-full border rounded-lg px-3 py-2 text-sm"
            placeholder="Net 30 jours. Intérêts de 2 % par mois..."
            maxLength={500}
            data-testid="settings-invoice-conditions"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Pied de page des factures</label>
          <textarea
            rows={2}
            value={settings.invoice_footer || ""}
            onChange={(e) => update("invoice_footer", e.target.value)}
            className="w-full border rounded-lg px-3 py-2 text-sm"
            placeholder="Merci de votre confiance."
            maxLength={500}
            data-testid="settings-invoice-footer"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Notes par défaut (nouvelle facture)</label>
          <textarea
            rows={2}
            value={settings.invoice_notes_default || ""}
            onChange={(e) => update("invoice_notes_default", e.target.value)}
            className="w-full border rounded-lg px-3 py-2 text-sm"
            placeholder="Pré-remplies lors de la création d'une facture."
            maxLength={1000}
            data-testid="settings-invoice-notes-default"
          />
        </div>
      </section>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-800 flex items-start gap-2">
        <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
        <span>Ces paramètres sont appliqués immédiatement aux PDF générés via les boutons "PDF" ou "Aperçu" dans une facture.</span>
      </div>

      {!isValid && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-xs text-red-800 flex items-start gap-2">
          <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>Corrigez les champs en erreur avant de sauvegarder.</span>
        </div>
      )}

      <button
        onClick={() => onSave(INVOICE_FIELDS)}
        disabled={!isValid}
        className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
        data-testid="settings-save-invoice"
      >
        <Save className="w-4 h-4" /> Sauvegarder
      </button>
    </div>
  );
}
